import { FC, useState, useEffect } from 'react';
import { Input } from '@shadcn/ui/components/ui/input';
import { Button } from '@shadcn/ui/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { cn } from '@shadcn/ui/lib/utils';

interface DiscountCodeProps {
    couponData: {
        code: string;
        isValid: boolean;
        hasCouponCodeApplied: boolean;
    } | null;
    onCouponCodeChange: (code: string) => Promise<void>;
    isLoading?: boolean;
}

export const DiscountCode: FC<DiscountCodeProps> = ({ couponData, onCouponCodeChange, isLoading = false }) => {
    const [inputValue, setInputValue] = useState<string | null>(couponData?.code);
    const [isDirty, setIsDirty] = useState(false);
    const [validating, setValidating] = useState(false);

    // Sync input value with parent when couponCode changes
    useEffect(() => {
        if (couponData?.code !== inputValue && !isDirty) {
            setInputValue(couponData?.code);
        }
    }, [couponData?.code]);

    const handleApply = async () => {
        if (!inputValue) return;

        setValidating(true);
        try {
            await onCouponCodeChange(inputValue);
        } finally {
            setValidating(false);
            setIsDirty(false);
        }
    };

    const showValidationStatus = couponData?.code && !isDirty;
    const isApplyDisabled = !inputValue || isLoading || validating || (!isDirty && inputValue === couponData?.code);

    return (
        <div className="flex flex-col gap-2">
            <span className="text-sm text-text-tertiary font-medium">Promo Code</span>
            <div className="flex items-center gap-2">
                <Input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        setIsDirty(true);
                    }}
                    className="flex-1"
                    placeholder="Enter discount code"
                    disabled={validating || isLoading}
                />
                <Button onClick={handleApply} disabled={isApplyDisabled} size="sm">
                    {validating ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            Validating
                        </>
                    ) : (
                        'Apply'
                    )}
                </Button>
            </div>
            {showValidationStatus && (
                <span
                    className={cn(
                        'flex items-center text-sm font-medium',
                        couponData?.isValid && couponData?.hasCouponCodeApplied
                            ? 'text-success'
                            : couponData?.isValid && !couponData?.hasCouponCodeApplied
                            ? 'text-warning'
                            : 'text-destructive'
                    )}
                >
                    {couponData?.hasCouponCodeApplied ? (
                        <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Promo code applied
                        </>
                    ) : couponData?.isValid && !couponData?.hasCouponCodeApplied ? (
                        <>
                            <XCircle className="w-4 h-4 mr-1" />
                            Promo code seems valid, but for a different plan or billing frequency. Please verify the
                            selected plan and billing frequency.
                        </>
                    ) : (
                        <>
                            <XCircle className="w-4 h-4 mr-1" />
                            Looks like this code isn’t valid.
                        </>
                    )}
                </span>
            )}
        </div>
    );
};
